import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { CaseMenu, AskLabel } from "./CaseMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, todayLocal, formatValue, formatAttr, keyFromLabel, dedupeKeys,
  COLLECTION_KIND_PRESETS, CONDITION_PRESETS, SCHEMA_PRESETS,
  type Collection, type Item, type CollectionSet, type AttrField, type AttrValues,
  type CollectionSignals, type FactTone,
} from "./store";
import { runAction, openWebLogin, BrainError, describeItem, identifyItem, type BrainErrorCode, type CurioAction } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { ProvenanceTimeline } from "./ProvenanceTimeline";
import { buildExport } from "./export";
import { buildCatalogDocument, catalogFilename } from "./catalogExport";
import { CollectionIntelligence } from "./CollectionIntelligence";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, X, Lock, WifiOff, RefreshCw, Plus, ChevronLeft, ImageIcon,
  Layers, Heart, ScrollText, Compass, Lightbulb, Boxes, Pencil, Coins, CheckCircle2,
  CalendarClock, Tag, LayoutGrid, List as ListIcon, Search, Camera,
} from "lucide-react";
import kindMineral from "./assets/kind-mineral-light.svg";
import kindMineralDark from "./assets/kind-mineral-dark.svg";
import kindVinyl from "./assets/kind-vinyl-light.svg";
import kindVinylDark from "./assets/kind-vinyl-dark.svg";
import kindCoin from "./assets/kind-coin-light.svg";
import kindCoinDark from "./assets/kind-coin-dark.svg";
import kindCard from "./assets/kind-card-light.svg";
import kindCardDark from "./assets/kind-card-dark.svg";
import kindBook from "./assets/kind-book-light.svg";
import kindBookDark from "./assets/kind-book-dark.svg";
import kindStamp from "./assets/kind-stamp-light.svg";
import kindStampDark from "./assets/kind-stamp-dark.svg";
import kindFigure from "./assets/kind-figure-light.svg";
import kindFigureDark from "./assets/kind-figure-dark.svg";
import kindGeneric from "./assets/kind-generic-light.svg";
import kindGenericDark from "./assets/kind-generic-dark.svg";
import huntCalipers from "./assets/hunt-calipers-light.svg";
import huntCalipersDark from "./assets/hunt-calipers-dark.svg";
import emptyNoCollections from "./assets/empty-no-collections-light.svg";
import emptyNoCollectionsDark from "./assets/empty-no-collections-dark.svg";
import emptyCollectionArt from "./assets/empty-collection-light.svg";
import emptyCollectionArtDark from "./assets/empty-collection-dark.svg";

// ── Drawn kind-marks (the identity's object vocabulary) ──────────────────────
// A collection's own `kind` is free text the user typed, so the map matches on
// substrings and falls back to the generic specimen mark — never an emoji.
const KIND_MARKS: [RegExp, string, string][] = [
  [/rock|mineral|crystal|gem|fossil|agate|stone/i, kindMineral, kindMineralDark],
  [/vinyl|record|lp|album|music|cd\b/i, kindVinyl, kindVinylDark],
  [/coin|currency|note|bill|numismat/i, kindCoin, kindCoinDark],
  [/card|tcg|pokemon|trading/i, kindCard, kindCardDark],
  [/book|comic|manga|magazine|zine/i, kindBook, kindBookDark],
  [/stamp|postal|philatel/i, kindStamp, kindStampDark],
  [/figure|toy|funko|model|doll|miniature/i, kindFigure, kindFigureDark],
];
function marksFor(kind: string): [string, string] {
  const hit = KIND_MARKS.find(([re]) => re.test(kind || ""));
  return hit ? [hit[1], hit[2]] : [kindGeneric, kindGenericDark];
}
/** Renders both colourways; CSS shows the one matching the colour scheme. */
function ThemedArt({ light, dark, className, style }: { light: string; dark: string; className?: string; style?: React.CSSProperties }) {
  return (
    <>
      <img className={`mark-light ${className ?? ""}`} src={light} alt="" style={style} />
      <img className={`mark-dark ${className ?? ""}`} src={dark} alt="" style={style} />
    </>
  );
}
function KindMark({ kind, className, style }: { kind: string; className?: string; style?: React.CSSProperties }) {
  const [light, dark] = marksFor(kind);
  return <ThemedArt light={light} dark={dark} className={className} style={style} />;
}

export const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }) : "";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i), #62). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-curio://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-curio://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable (#62).
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Curio"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootSplash />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function BootSplash() {
  return (
    <div style={{ height: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <div className="empty-art" style={{ width: 64, height: 64, borderRadius: 18 }}><Boxes size={30} /></div>
        <div className="skeleton-line" style={{ width: 120, height: 10 }} />
      </div>
    </div>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><Boxes size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>Oriora Curio</div>
        <h1 className="serif" style={{ fontSize: "1.55rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>Everything you collect, catalogued</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Rocks, vinyl, coins, scarves, cards — give each collection its own fields, no fixed form.
          {" "}It all stays on this device unless you back it up;
          {" "}Oriora reads your catalogue back to you through your own account.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-curio");
              else if (p === "macos") await signInMac("oriora-curio");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-curio/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "1rem", lineHeight: 1.5 }}>
          Opens Oriora to sign in, then returns here.
        </p>
      </div>
    </main>
  );
}

// Safe-area aware page padding — clears the iPhone notch + home indicator (viewport-fit=cover).
const PAGE_PAD = "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))";

function OfflineBanner() {
  return (
    <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
      <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span><strong>Offline.</strong> AI actions are paused — your catalogue still saves on this device.</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Home — the list of collections + create-collection. Selecting one opens CollectionView.
// ─────────────────────────────────────────────────────────────────────────────
interface HuntRow {
  id: string;
  name: string;
  collectionId: string;
  collection: string;
  have: number;
  target: number;
  want: number;
  remaining: number;
}

// The hunt (closest-to-complete sets) now renders per shelf on Home as a chase line —
// see ShelfCard. The old standalone HuntBoard banner is gone with the a3 restructure.
function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [homeChatOpen, setHomeChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const [hunt, setHunt] = useState<HuntRow[]>([]);
  // Up to 4 item photos per collection — the shelf face. Items without a photo
  // contribute nothing here; the card falls back to the collection's kind-mark.
  const [faces, setFaces] = useState<Record<string, string[]>>({});

  const reload = useCallback(async () => {
    const cols = await store.listCollections();
    setCollections(cols);
    const all = await store.listItems();
    const c: Record<string, number> = {};
    for (const it of all) c[it.collection_id] = (c[it.collection_id] ?? 0) + 1;
    setCounts(c);
    const f: Record<string, string[]> = {};
    for (const it of all) {
      if (!it.photo_ref) continue;
      const arr = (f[it.collection_id] ??= []);
      if (arr.length < 4) arr.push(it.photo_ref);
    }
    setFaces(f);

    // Set completeness across EVERY collection. The figures (have / remaining / one-from-complete)
    // already existed in signals.rs but were only ever sent to the brain — the collector never saw
    // them, and the home screen ranked collections by created_at instead.
    const sets = (await Promise.all(cols.map((col) => store.listSets(col.id)))).flat();
    const haveBySet = new Map<string, number>();
    const wantBySet = new Map<string, number>();
    for (const it of all) {
      if (!it.set_id) continue;
      const m = it.wishlist ? wantBySet : haveBySet;
      m.set(it.set_id, (m.get(it.set_id) ?? 0) + 1);
    }
    const byCollection = new Map(cols.map((col) => [col.id, col.name]));
    setHunt(
      sets
        // A set with no target has no "remaining" — claiming one would be inventing a number.
        .filter((s) => typeof s.target_size === "number" && (s.target_size as number) > 0)
        .map((s) => {
          const have = haveBySet.get(s.id) ?? 0;
          const target = s.target_size as number;
          return {
            id: s.id,
            name: s.name,
            collectionId: s.collection_id,
            collection: byCollection.get(s.collection_id) ?? "",
            have,
            target,
            want: wantBySet.get(s.id) ?? 0,
            remaining: Math.max(0, target - have),
          };
        }),
    );
    setLoaded(true);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // The hunt survives the restructure as a per-shelf chase line rather than a
  // banner: for each collection, the set it is CLOSEST to finishing. Complete
  // sets carry no chase — nothing to go after.
  const chaseByCollection = useMemo(() => {
    const m = new Map<string, HuntRow>();
    for (const r of hunt) {
      if (r.remaining <= 0) continue;
      const cur = m.get(r.collectionId);
      if (!cur || r.remaining < cur.remaining) m.set(r.collectionId, r);
    }
    return m;
  }, [hunt]);

  // Native-menu actions (macOS Actions menu): New collection (others handled inside CollectionView).
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      if (e.payload === "new_collection" && openId === null) setShowCreate(true);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, [openId]);

  if (openId) {
    const col = collections.find((c) => c.id === openId);
    if (col) {
      return <CollectionView collection={col} onBack={() => { setOpenId(null); void reload(); }} onChanged={reload} offline={offline} />;
    }
  }

  if (!loaded) return <BootSplash />;

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: PAGE_PAD }}>
      {offline && <OfflineBanner />}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The COLLECTIONS are the header
          — a catalogue app opens on the objects, object-first, not on the app's own name. The
          backup and account controls still have to be reachable, so they collapse into ONE lit
          case control on the collections' own head row. Do not reintroduce a wordmark strip, and
          do not split the control back into two circles. */}

      {collections.length === 0 ? (
        <>
        <div className="row" style={{ justifyContent: "flex-end", marginBottom: "0.75rem" }}>
          <CaseMenu
            backup={<BackupMenu appName="Curio" fileSlug="curio" appScheme="oriora-curio" hasData={false} onRestored={reload} />}
            account={<AccountMenu email={email} appSlug="oriora-curio" appName="Oriora Curio" onSignedOut={onSignedOut} />}
          />
        </div>
        <EmptyCollections onAdd={() => setShowCreate(true)} />
        <AskLabel onAsk={() => setHomeChatOpen(true)} sub="manage your collections" />
        </>
      ) : (
        <>
          {/* The shelves ARE the home. The collection's own objects (photos, or the
              drawn kind-mark until one is photographed) make the card face, so the
              catalogue app opens on the catalogue rather than on arithmetic about it. */}
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.75rem" }}>
            <span className="eyebrow" style={{ color: "var(--ink-3)" }}>Your collections</span>
            <div className="row" style={{ gap: "0.75rem" }}>
              <button className="quiet-add" onClick={() => setShowCreate(true)}><Plus size={12} /> New collection</button>
              <CaseMenu
                backup={
                  <BackupMenu
                    appName="Curio" fileSlug="curio" appScheme="oriora-curio"
                    hasData={collections.length > 0} onRestored={reload}
                  />
                }
                account={<AccountMenu email={email} appSlug="oriora-curio" appName="Oriora Curio" onSignedOut={onSignedOut} />}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.7rem" }}>
            {collections.map((c) => (
              <ShelfCard
                key={c.id}
                collection={c}
                count={counts[c.id] ?? 0}
                faces={faces[c.id] ?? []}
                chase={chaseByCollection.get(c.id) ?? null}
                onOpen={() => setOpenId(c.id)}
              />
            ))}
          </div>

          {/* 🔴 THE ASSISTANT ENTRY — a case label under the collections it describes, replacing
              the floating "✨ Chat" pill. onAsk opens the same sheet: moved, never removed. */}
          <AskLabel onAsk={() => setHomeChatOpen(true)} sub="manage your collections" />
        </>
      )}

      {showCreate && (
        <CreateCollection onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); void reload().then(() => setOpenId(id)); }} />
      )}

      {/* Home assistant — create / delete / move / suggest collections without opening one. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "✨ Chat" pill that used to launch this was DELETED —
          same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
          must never be: its entry is the .ocu-label case label, which calls this same open-state
          setter. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`ocu-sheet ${homeChatOpen ? "open" : ""}`} aria-hidden={!homeChatOpen}>
        <div className="ocu-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — manage your collections</span>
          <button className="link" onClick={() => setHomeChatOpen(false)}>Close ↓</button>
        </div>
        {homeChatOpen && <Chat collectionId={null} onChanged={reload} />}
      </aside>
    </main>
  );
}

// ── Shelf card — a collection shown as its own objects ───────────────────────
// Face = up to 4 of this collection's item photos; where a photo hasn't been
// added yet the drawn kind-mark stands in, so the shelf reads as objects from
// the very first item. Plate = name, kind + schema size, count. Chase = the
// closest-to-complete set, the answer a collector wants in a shop.
function ShelfCard({ collection, count, faces, chase, onOpen }: {
  collection: Collection; count: number; faces: string[]; chase: HuntRow | null; onOpen: () => void;
}) {
  const tiles = faces.length > 0 ? faces.slice(0, 4) : [null, null, null];
  return (
    <button className="shelf-card fade-up" onClick={onOpen}>
      <div className="shelf-face">
        {tiles.map((src, i) => (
          <div className="shelf-tile" key={src ?? `mark-${i}`}>
            {src
              ? <img src={src} alt="" />
              : <KindMark kind={collection.kind} className="kind-mark" style={{ width: "46%", height: "46%", opacity: 0.8 }} />}
          </div>
        ))}
      </div>
      <div className="shelf-plate">
        <div style={{ minWidth: 0 }}>
          <div className="shelf-name">{collection.name}</div>
          <div className="shelf-meta">
            {collection.kind || "Collection"}
            {collection.attr_schema.length > 0 && ` · ${collection.attr_schema.length} custom ${collection.attr_schema.length === 1 ? "field" : "fields"}`}
          </div>
        </div>
        <span className="shelf-count tnum">{count} {count === 1 ? "item" : "items"}</span>
      </div>
      {chase && (
        <div className="shelf-chase">
          <span className="row" style={{ gap: "0.4rem", minWidth: 0, flexWrap: "nowrap" }}>
            <ThemedArt light={huntCalipers} dark={huntCalipersDark} className="glyph" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <strong>{chase.name}</strong> — {chase.have} of {chase.target}
            </span>
          </span>
          <span className="chase-remaining">{chase.remaining === 1 ? "1 to go" : `${chase.remaining} to go`}</span>
        </div>
      )}
    </button>
  );
}

function EmptyCollections({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="card fade-up" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
      <ThemedArt light={emptyNoCollections} dark={emptyNoCollectionsDark} className="empty-illo" />
      <div className="serif" style={{ fontSize: "1.4rem", margin: "1rem 0 0.4rem", letterSpacing: "-0.02em" }}>Catalogue anything you keep</div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "1.5rem", lineHeight: 1.6, maxWidth: 330, marginInline: "auto" }}>
        Rocks, vinyl, coins, scarves, cards — pick a type, then name your own fields for it. Nothing here has a fixed form.
        {" "}It all stays on this device unless you back it up.
      </div>
      <button className="btn btn-primary btn-pill" style={{ margin: "0 auto", minHeight: 46, padding: "0 1.5rem" }} onClick={onAdd}><Plus size={17} /> Create your first collection</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create collection — name + type + the CUSTOM ATTRIBUTE SCHEMA (the heart of the app).
// Picking a preset type seeds a starter schema the user can edit freely.
// ─────────────────────────────────────────────────────────────────────────────
function CreateCollection({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [fields, setFields] = useState<AttrField[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function pickPreset(k: string) {
    setKind(k);
    setFields(SCHEMA_PRESETS[k] ? SCHEMA_PRESETS[k].map((f) => ({ ...f })) : []);
    if (!name.trim()) setName(k);
  }

  async function create() {
    if (!name.trim()) { setErr("Give your collection a name."); return; }
    setBusy(true); setErr(null);
    try {
      const schema = dedupeKeys(
        fields
          .filter((f) => f.label.trim())
          .map((f) => ({ ...f, label: f.label.trim(), key: f.key || keyFromLabel(f.label) })),
      );
      const c = await store.createCollection({ name: name.trim(), kind: kind.trim(), attr_schema: schema });
      onCreated(c.id);
    } catch (e) {
      setErr(`Could not create — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <BottomSheet title="New collection" onClose={onClose}>
      <Field label="Name">
        <input autoFocus placeholder="e.g. My agates, Jazz LPs, Roman coins" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <FieldLabel>Type</FieldLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {COLLECTION_KIND_PRESETS.map((k) => (
          <button key={k} type="button" className={`chip${kind === k ? " selected" : ""}`} onClick={() => pickPreset(k)}>{k}</button>
        ))}
      </div>
      <input placeholder="…or type your own" value={kind} onChange={(e) => setKind(e.target.value)} style={{ marginBottom: "1rem" }} />

      <FieldLabel>Custom fields <span className="muted" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— each item gets these</span></FieldLabel>
      <SchemaEditor fields={fields} onChange={setFields} />

      {err && <div className="notice notice-err" style={{ margin: "0.75rem 0 0.25rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={create} saving={busy} canSave={!!name.trim()} saveLabel="Create collection" />
    </BottomSheet>
  );
}

const FIELD_KINDS: { kind: AttrField["kind"]; label: string }[] = [
  { kind: "text", label: "Text" },
  { kind: "number", label: "Number" },
  { kind: "select", label: "Choice" },
  { kind: "date", label: "Date" },
  { kind: "bool", label: "Yes/No" },
];

// Reusable editor for a collection's custom-attribute schema (add / rename / retype / remove).
function SchemaEditor({ fields, onChange }: { fields: AttrField[]; onChange: (f: AttrField[]) => void }) {
  function update(i: number, patch: Partial<AttrField>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function remove(i: number) { onChange(fields.filter((_, idx) => idx !== i)); }
  function add() { onChange([...fields, { key: "", label: "", kind: "text" }]); }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
      {fields.map((f, i) => (
        <div key={i} className="card" style={{ padding: "0.6rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.4rem", background: "var(--elev-2)", boxShadow: "none" }}>
          <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
            <input placeholder="Field name (e.g. Locality)" value={f.label} onChange={(e) => update(i, { label: e.target.value })} style={{ flex: 2 }} />
            <select value={f.kind} onChange={(e) => update(i, { kind: e.target.value as AttrField["kind"] })} style={{ flex: 1 }}>
              {FIELD_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </select>
            <button type="button" className="tile-del" title="Remove field" aria-label="Remove field" onClick={() => remove(i)}><X size={15} /></button>
          </div>
          {f.kind === "select" && (
            <input placeholder="Choices, comma-separated (e.g. Common, Rare)" value={(f.options ?? []).join(", ")}
              onChange={(e) => update(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          )}
        </div>
      ))}
      <button type="button" className="btn btn-pill" onClick={add} style={{ justifyContent: "center" }}><Plus size={15} /> Add field</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Collection view — hero, deterministic Insights dashboard, sets, items, AI actions.
// ─────────────────────────────────────────────────────────────────────────────
function CollectionView({ collection, onBack, onChanged, offline }: {
  collection: Collection; onBack: () => void; onChanged: () => Promise<void>; offline: boolean;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [sets, setSets] = useState<CollectionSet[]>([]);
  const [editing, setEditing] = useState<Item | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  // Set when the editor was opened from "Add by photo" — it then leads with the picker.
  const [addWithPhoto, setAddWithPhoto] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Item | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const [showIntelligence, setShowIntelligence] = useState(false);
  const [catalogMsg, setCatalogMsg] = useState<string | null>(null);
  const [showSets, setShowSets] = useState(false);
  const [filterWishlist, setFilterWishlist] = useState<"all" | "have" | "want">("all");
  // Photo-forward grid is the DEFAULT (row 2b/1d) — matches how the category leaders (CLZ's
  // Cover/Shelves view, Collectr's portfolio cards) actually browse a collection; a text list is
  // the power-user fallback, not the primary mode. Persisted per-device so the choice sticks.
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    try { return (localStorage.getItem("oriora.curio.view") as "grid" | "list") || "grid"; } catch { return "grid"; }
  });
  useEffect(() => { try { localStorage.setItem("oriora.curio.view", viewMode); } catch { /* ignore */ } }, [viewMode]);
  const [chatOpen, setChatOpen] = useState(false);

  const reload = useCallback(async () => {
    setItems(await store.listItems(collection.id));
    setSets(await store.listSets(collection.id));
  }, [collection.id]);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions while inside a collection.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add_item": setEditing(null); setShowAdd(true); break;
        case "recap": setShowInsights(true); break;
        case "export": doExport(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sets]);

  const have = useMemo(() => items.filter((i) => !i.wishlist), [items]);
  const want = useMemo(() => items.filter((i) => i.wishlist), [items]);
  // Distinct sources the user has already typed → autocomplete the Source field when adding items
  // (universal, purely the user's OWN data, no external lookup — the COLLECTION fence stays intact).
  const knownSources = useMemo(
    () => Array.from(new Set(items.map((i) => (i.source ?? "").trim()).filter(Boolean))).sort(),
    [items],
  );
  const totalValue = useMemo(() => have.reduce((s, i) => s + (i.cost ?? 0), 0), [have]);
  const setNameMap = useMemo(() => new Map(sets.map((s) => [s.id, s.name])), [sets]);

  const shown = useMemo(() => {
    if (filterWishlist === "have") return have;
    if (filterWishlist === "want") return want;
    return items;
  }, [items, have, want, filterWishlist]);

  function doExport() {
    if (items.length === 0) return;
    const { csv, filename } = buildExport(collection, items, sets);
    void (async () => {
      // iOS has no desktop save dialog → saveFileDialog is a silent no-op. Use the native
      // share sheet (works on iPhone + iPad) to hand the CSV off to Files / Mail / etc.
      if (platform() === "ios") {
        try {
          const file = new File([csv], filename, { type: "text/csv" });
          const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
          if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
            await nav.share({ files: [file], title: collection.name });
            return;
          }
          if (nav.share) { await nav.share({ title: collection.name, text: csv }); return; }
        } catch { /* user cancelled the share sheet, or share unavailable → fall through */ }
        return;
      }
      const p = await saveFileDialog({ defaultPath: filename });
      if (p) await writeTextFile(p, csv);
    })();
  }

  function doCatalogExport() {
    if (items.length === 0) return;
    const doc = buildCatalogDocument(collection, items, sets);
    const filename = catalogFilename(collection.name);
    void (async () => {
      // Same share-sheet pattern as the CSV export above — works unconditionally on iOS,
      // no dependency on a connected GitHub/Drive account.
      if (platform() === "ios") {
        try {
          const file = new File([doc], filename, { type: "text/markdown" });
          const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
          if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
            await nav.share({ files: [file], title: collection.name });
            return;
          }
          if (nav.share) { await nav.share({ title: collection.name, text: doc }); return; }
        } catch { /* user cancelled the share sheet, or share unavailable → fall through */ }
        return;
      }
      const p = await saveFileDialog({ defaultPath: filename });
      if (p) { await writeTextFile(p, doc); setCatalogMsg("Catalog document saved."); }
    })();
  }

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteItem(id);
    await reload();
    await onChanged();
  }

  const hasItems = have.length > 0;

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: PAGE_PAD }}>
      {offline && <OfflineBanner />}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1rem" }}>
        <button className="btn btn-sm btn-pill" onClick={onBack}><ChevronLeft size={15} /> All</button>
        <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on device</span>
      </header>

      {/* Hero — collection identity only. The counts moved into one summary line
          below; four stat tiles between the name and the objects was the a3 defect. */}
      <div className="hero fade-up" style={{ marginBottom: "0.85rem" }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="serif" style={{ fontSize: "1.45rem", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.01em" }}>{collection.name}</div>
          <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85, marginTop: 3 }}>{collection.kind || "Collection"}</div>
        </div>
      </div>

      {/* 🔴 THE ASSISTANT ENTRY on this surface — the case label for THIS collection, replacing
          the second floating "✨ Chat" pill. The collection screen had its own pill, so it needs
          its own replacement: the AI must stay reachable everywhere it already was. */}
      <AskLabel onAsk={() => setChatOpen(true)} sub="add · find · summarize" />

      {hasItems && (
        <div className="summary-line tnum">
          <span><strong>{have.length}</strong> {have.length === 1 ? "item" : "items"}</span>
          {totalValue > 0 && <><span className="dot">·</span><span><strong>{formatValue(totalValue)}</strong> logged</span></>}
          {want.length > 0 && <><span className="dot">·</span><span><strong>{want.length}</strong> on the wishlist</span></>}
        </div>
      )}

      {/* Sets — completeness strips, up near the top: "am I close?" is the
          question a collector opens the app with. */}
      {sets.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", marginBottom: "1.1rem" }}>
          {sets.map((s) => {
            const inSet = have.filter((i) => i.set_id === s.id);
            const haveN = inSet.length;
            const pct = s.target_size && s.target_size > 0 ? Math.min(100, Math.round((haveN / s.target_size) * 100)) : null;
            const done = s.target_size != null && haveN >= s.target_size;
            const mosaic = inSet.filter((i) => i.photo_ref).slice(0, 4);
            return (
              <div key={s.id} className="card set-strip">
                <div className="row" style={{ gap: "0.7rem", alignItems: "flex-start" }}>
                  {mosaic.length > 0 && (
                    <div className="set-mosaic">
                      {mosaic.map((it) => {
                        const src = it.photo_ref!.startsWith("http") || it.photo_ref!.startsWith("data:") ? it.photo_ref! : `file://${it.photo_ref}`;
                        return <img key={it.id} src={src} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }} />;
                      })}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="set-row">
                      <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{s.name}</span>
                      <span className="muted tnum row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem" }}>
                        {done && <CheckCircle2 size={13} style={{ color: "var(--success)" }} />}
                        {s.target_size ? `${haveN} of ${s.target_size}` : `${haveN} logged`}
                      </span>
                    </div>
                    {pct !== null && (
                      <div className="bar-track" style={{ marginTop: 9 }}>
                        <div className={`bar-fill${done ? " done" : ""}`} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Camera-first add — the leaders all make adding an object effortless and
          visual; the full form is one tap behind it. */}
      <div className="add-row">
        <button className="add-camera" onClick={() => { setEditing(null); setAddWithPhoto(true); setShowAdd(true); }}>
          <Camera size={17} /> Add by photo
        </button>
        <button className="add-manual" title="Add manually" aria-label="Add manually"
          onClick={() => { setEditing(null); setAddWithPhoto(false); setShowAdd(true); }}>
          <Plus size={16} />
        </button>
      </div>

      {/* Items — the objects lead; both AI panels sit below them. */}
      <div className="sec-head" style={{ marginTop: "0.4rem" }}>
        <h2><Boxes size={17} style={{ color: "var(--accent)" }} /> Items</h2>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
          {items.length > 0 && <button className="btn btn-sm btn-pill" onClick={doExport}>Export CSV</button>}
          {items.length > 0 && <button className="btn btn-sm btn-pill" onClick={doCatalogExport}>Export catalog</button>}
        </div>
      </div>
      {catalogMsg && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.6rem" }}>{catalogMsg}</div>}
      {items.length > 0 && (
        <div className="row" style={{ gap: "0.4rem", marginBottom: "0.8rem", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div className="row" style={{ gap: "0.4rem" }}>
            {(["all", "have", "want"] as const).map((f) => (
              <button key={f} className={`chip${filterWishlist === f ? " selected" : ""}`} onClick={() => setFilterWishlist(f)}>
                {f === "all" ? `All ${items.length}` : f === "have" ? `Have ${have.length}` : `Wishlist ${want.length}`}
              </button>
            ))}
          </div>
          <div className="view-toggle" role="group" aria-label="View">
            <button className={viewMode === "grid" ? "active" : ""} title="Grid view" aria-label="Grid view" onClick={() => setViewMode("grid")}><LayoutGrid size={15} /></button>
            <button className={viewMode === "list" ? "active" : ""} title="List view" aria-label="List view" onClick={() => setViewMode("list")}><ListIcon size={15} /></button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card fade-up" style={{ padding: "2rem 1.5rem", textAlign: "center" }}>
          <ThemedArt light={emptyCollectionArt} dark={emptyCollectionArtDark} className="empty-illo" style={{ width: 96, height: 96 }} />
          <div className="serif" style={{ fontSize: "1.15rem", margin: "0.75rem 0 0.35rem", fontWeight: 700 }}>Log your first item</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55 }}>Name it, add a photo and the value you paid, and fill its fields.</div>
        </div>
      ) : shown.length === 0 ? (
        <EmptyTile icon={<Heart size={26} />} title="Nothing here yet" sub={filterWishlist === "want" ? "Mark items as “want” to build a wishlist." : "No items in this view."} />
      ) : viewMode === "grid" ? (
        <div className="item-grid">
          {shown.map((it) => (
            <ItemGridCard key={it.id} item={it} collection={collection} setName={it.set_id ? setNameMap.get(it.set_id) : undefined}
              onEdit={() => { setEditing(it); setShowAdd(true); }} onDelete={() => setConfirmDelete(it)} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {shown.map((it) => (
            <ItemTile key={it.id} item={it} collection={collection} setName={it.set_id ? setNameMap.get(it.set_id) : undefined}
              onEdit={() => { setEditing(it); setShowAdd(true); }} onDelete={() => setConfirmDelete(it)} />
          ))}
        </div>
      )}

      {/* Sets manager entry — kept reachable now that the section is a strip. */}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "0.9rem" }}>
        <button className="btn btn-sm btn-pill" onClick={() => setShowSets(true)}><Layers size={13} /> Manage sets</button>
      </div>

      {/* Deterministic (free) collection stats — below the objects. */}
      {hasItems && <CollectionStats collection={collection} itemCount={items.length} />}

      {/* The AI tour entry — Recap · Reflect · Ideas. */}
      <button className="recap fade-up" onClick={() => setShowInsights(true)}
        style={{ width: "100%", textAlign: "left", padding: "1.1rem 1.25rem", marginBottom: "1.1rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.7rem" }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}><Sparkles size={14} /> Insights</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>
            {hasItems ? "Recap, Reflect, and Ideas — a guided tour of what you've logged." : "Log a few items first, then take a guided tour of your catalogue."}
          </div>
        </div>
        <span className="chip" style={{ flexShrink: 0 }}>Open</span>
      </button>

      <button className="recap fade-up" onClick={() => setShowIntelligence(true)}
        style={{ width: "100%", textAlign: "left", padding: "1.1rem 1.25rem", marginBottom: "1.1rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.7rem" }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}><Search size={14} /> Collection Intelligence</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>
            Find similar collections, compare a link, or look up an item's value — cited, from the open web.
          </div>
        </div>
        <span className="chip" style={{ flexShrink: 0 }}>Open</span>
      </button>

      {showAdd && (
        <ItemEditor collection={collection} sets={sets} editing={editing} knownSources={knownSources} startWithPhoto={addWithPhoto}
          onClose={() => { setShowAdd(false); setEditing(null); setAddWithPhoto(false); }}
          onSaved={async () => { setShowAdd(false); setEditing(null); setAddWithPhoto(false); await reload(); await onChanged(); }}
          onSetsChanged={reload} />
      )}

      {showSets && (
        <SetsManager collection={collection} sets={sets} onClose={() => setShowSets(false)} onChanged={reload} />
      )}

      {showInsights && (
        <Insights collection={collection} hasItems={hasItems} offline={offline} onClose={() => setShowInsights(false)} />
      )}

      {showIntelligence && (
        <CollectionIntelligence collection={collection} items={items} hasItems={hasItems} offline={offline} onClose={() => setShowIntelligence(false)} />
      )}

      {confirmDelete && (
        <CenterModal title="Delete item?" onClose={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            Delete “{confirmDelete.name}”? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </CenterModal>
      )}

      {/* In-app assistant — type to add, find, mark, or summarize items in this collection. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "✨ Chat" pill that used to launch this was DELETED —
          same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
          must never be: its entry is the .ocu-label case label, which calls this same open-state
          setter. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`ocu-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="ocu-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or summarize</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat collectionId={collection.id} onChanged={async () => { await reload(); await onChanged(); }} />
      </aside>
    </main>
  );
}

// ── Deterministic stats dashboard (computed on-device from the signal slice, no AI call) ──
function CollectionStats({ collection, itemCount }: { collection: Collection; itemCount: number }) {
  const [signals, setSignals] = useState<CollectionSignals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store.computeSignals(collection.id, todayLocal())
      .then((s) => { if (alive) { setSignals(s); setLoading(false); } })
      .catch(() => { if (alive) { setSignals(null); setLoading(false); } });
    return () => { alive = false; };
  }, [collection.id, itemCount]);

  if (loading) {
    return (
      <div className="card" style={{ padding: "1rem", marginBottom: "1.1rem" }}>
        <div className="skeleton-block" style={{ height: 78 }} />
      </div>
    );
  }
  if (!signals) return null;

  const ss = signals.set_summary;
  const focus = signals.focus;
  const cadence = signals.acquisition_cadence;
  const topVal = signals.most_valued[0];

  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      {/* The four stat tiles are GONE (a3 restructure) — their headline figures now
          read as one summary line above the objects. What stays here is what a line
          cannot carry: the closest-open set bar and the grounded fact rows below. */}
      {/* Closest-to-complete set (the most satisfying number a catalogue gives). */}
      {ss.closest_open && (
        <div className="card" style={{ padding: "0.8rem 1rem", marginTop: "0.7rem" }}>
          <div className="set-row" style={{ marginBottom: 9 }}>
            <span className="row" style={{ gap: "0.4rem", fontSize: "var(--fs-sm)", fontWeight: 600 }}>
              <Layers size={14} style={{ color: "var(--accent)" }} /> Closest set · {ss.closest_open.name}
            </span>
            <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>{ss.closest_open.have} of {ss.closest_open.target_size}{ss.closest_open.remaining === 1 ? " · one to go" : ` · ${ss.closest_open.remaining} to go`}</span>
          </div>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${Math.min(100, Math.round((ss.closest_open.have / ss.closest_open.target_size) * 100))}%` }} /></div>
        </div>
      )}

      {/* Focus + standout value as quiet supporting facts. */}
      {(focus || topVal) && (
        <div className="card" style={{ padding: "0.7rem 1rem", marginTop: "0.7rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {focus && (
            <div className="fact" style={{ borderTop: "none", padding: 0 }}>
              <Tag size={14} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
              <span className="fact-text">Leans <strong>{focus.value_label}</strong> — {focus.count} of your {focus.field_label.toLowerCase()} entries ({Math.round(focus.share_pct)}%).</span>
            </div>
          )}
          {topVal && (
            <div className="fact" style={{ borderTop: focus ? undefined : "none", padding: focus ? undefined : 0 }}>
              <Coins size={14} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
              <span className="fact-text">Highest value you logged: <strong>{topVal.name}</strong> at {formatValue(topVal.logged_value)} <span className="muted">(your own figure)</span>.</span>
            </div>
          )}
        </div>
      )}

      {/* More free intelligence — the SAME on-device signals.rs headline_facts the paid Insights
          call is grounded in (cadence, duplicates, wishlist patterns…), surfaced here at ZERO
          extra cost since compute_signals already ran for the stat tiles above. Answers Bogdan's
          "AI-enhanced maximizing" ask honestly: more of the app's real intelligence visible for
          free, not a new billed feature bolted on without proper design/testing. */}
      {/* "focus" and "value" kinds are already shown bespoke above (Leans.../Highest value...) —
          exclude by KIND, not position, since how many facts fire before them varies (e.g. a
          second completeness fact only appears when a set is genuinely one-from-done). */}
      <FactList signals={signals} excludeKinds={["focus", "value"]} standalone />
    </div>
  );
}

function ItemTile({ item, collection, setName, onEdit, onDelete }: {
  item: Item; collection: Collection; setName?: string; onEdit: () => void; onDelete: () => void;
}) {
  const [viewing, setViewing] = useState(false);
  // Up to two filled custom attrs as a quick subtitle.
  const attrBits = collection.attr_schema
    .map((f) => ({ f, v: formatAttr(item.attrs?.[f.key]) }))
    .filter((x) => x.v)
    .slice(0, 2)
    .map((x) => `${x.f.label}: ${x.v}`);

  const photoSrc = item.photo_ref
    ? (item.photo_ref.startsWith("http") || item.photo_ref.startsWith("data:") ? item.photo_ref : `file://${item.photo_ref}`)
    : "";

  return (
    <div className="tile" onClick={onEdit} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
      {item.photo_ref ? (
        <img className="thumb" src={photoSrc} alt="" title="Tap to view" style={{ cursor: "zoom-in" }}
          onClick={(e) => { e.stopPropagation(); setViewing(true); }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
      ) : (
        <span className="thumb"><ImageIcon size={20} /></span>
      )}
      {viewing && photoSrc && (
        <PhotoViewer photos={[{ src: photoSrc, alt: item.name }]} onClose={() => setViewing(false)} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
          {item.wishlist && <span className="chip" style={{ minHeight: "auto", padding: "0.1rem 0.4rem", fontSize: 10, gap: "0.2rem", color: "var(--accent)", borderColor: "var(--accent-line)", flexShrink: 0 }}><Heart size={10} /> want</span>}
        </div>
        {attrBits.length > 0 && <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attrBits.join(" · ")}</div>}
        <div className="muted tnum" style={{ fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.cost !== null && <span>{formatValue(item.cost)}</span>}
          {item.cost !== null && (setName || item.acquired_on) && " · "}
          {setName && <span>{setName}</span>}
          {setName && item.acquired_on && " · "}
          {item.acquired_on && <span>{fmtDate(item.acquired_on)}</span>}
        </div>
      </div>
      <button className="tile-del" title="Delete item" aria-label="Delete item" onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={16} />
      </button>
    </div>
  );
}

// ── Photo-forward grid card (row 2b/1d bones) — the DEFAULT browsing mode, matching how the
// real category leaders (CLZ Cover/Shelves view, Collectr's portfolio cards) actually present a
// collection: the photo dominates, value/wishlist render as overlay badges, metadata is a caption
// underneath. .tile (ItemTile above) stays as the List-view fallback for power users.
function ItemGridCard({ item, collection, setName, onEdit, onDelete }: {
  item: Item; collection: Collection; setName?: string; onEdit: () => void; onDelete: () => void;
}) {
  const photoSrc = item.photo_ref
    ? (item.photo_ref.startsWith("http") || item.photo_ref.startsWith("data:") ? item.photo_ref : `file://${item.photo_ref}`)
    : "";
  const attrBit = collection.attr_schema
    .map((f) => formatAttr(item.attrs?.[f.key]))
    .find((v) => v);
  // The collection's own schema is the thing no fixed-schema competitor has —
  // show the first filled field on the card face rather than hiding it in the editor.
  const schemaField = collection.attr_schema.find((f) => formatAttr(item.attrs?.[f.key]));
  const schemaValue = schemaField ? formatAttr(item.attrs?.[schemaField.key]) : "";

  return (
    <div className="grid-card" onClick={onEdit} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
      <div className="grid-photo">
        {photoSrc ? (
          <img src={photoSrc} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
        ) : (
          <KindMark kind={collection.kind} className="kind-mark" />
        )}
        {item.wishlist && <span className="grid-badge"><Heart size={10} /> want</span>}
        {item.cost !== null && <span className="grid-value">{formatValue(item.cost)}</span>}
        <button className="tile-del" title="Delete item" aria-label="Delete item"
          style={{ position: "absolute", top: "0.3rem", left: "0.3rem", background: "rgba(20,16,8,0.55)", color: "#fff" }}
          onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
          <Trash2 size={13} />
        </button>
      </div>
      <div className="grid-body">
        <span className="grid-name">{item.name}</span>
        <div className="grid-sub">{setName || attrBit || (item.acquired_on ? fmtDate(item.acquired_on) : collection.kind)}</div>
        {schemaField && (
          <div style={{ marginTop: 6 }}>
            <span className="schema-chip"><span className="k">{schemaField.label}</span>{schemaValue}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Add / edit an item — built-in fields + the collection's CUSTOM attributes + set + value.
// ─────────────────────────────────────────────────────────────────────────────
function ItemEditor({ collection, sets, editing, knownSources, startWithPhoto, onClose, onSaved, onSetsChanged }: {
  collection: Collection; sets: CollectionSet[]; editing: Item | null; knownSources: string[]; startWithPhoto?: boolean;
  onClose: () => void; onSaved: () => void; onSetsChanged: () => Promise<void>;
}) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  const [acquired, setAcquired] = useState(editing?.acquired_on ?? "");
  const [source, setSource] = useState(editing?.source ?? "");
  const [costStr, setCostStr] = useState(editing?.cost != null ? String(editing.cost) : "");
  const [condition, setCondition] = useState(editing?.condition ?? "");
  const [wishlist, setWishlist] = useState(editing?.wishlist ?? false);
  const [setId, setSetId] = useState(editing?.set_id ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [attrs, setAttrs] = useState<AttrValues>(editing?.attrs ?? {});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [descBusy, setDescBusy] = useState(false);
  const [descErr, setDescErr] = useState<string | null>(null);
  // Identify-from-photo (§5b 2026-08-19): one billed vision call drafts THIS collection's own
  // fields from the picked photo. Suggestions fill only what's still EMPTY — never overwrite
  // what the customer typed — and they review before saving. No value/appraisal ever (the fence).
  const [identBusy, setIdentBusy] = useState(false);
  const [identNote, setIdentNote] = useState<string | null>(null);
  async function identifyFromPhoto() {
    if (!photoRef.startsWith("data:")) { setIdentNote("Add a photo first — then I can read it."); return; }
    setIdentBusy(true); setIdentNote(null);
    try {
      const r = await identifyItem({
        image: photoRef,
        collection_name: collection.name,
        attributes: collection.attr_schema.map((f) => ({ key: f.key, label: f.label })),
      });
      if (!name.trim() && r.name) setName(r.name);
      let filled = 0;
      setAttrs((cur) => {
        const next = { ...cur };
        for (const f of collection.attr_schema) {
          const v = (r.values[f.key] ?? "").trim();
          const empty = next[f.key] == null || String(next[f.key]).trim() === "";
          if (v && empty && (f.kind === "text" || f.kind === "select" || f.kind === "number")) {
            if (f.kind === "number") { const n = Number(v.replace(/[^0-9.\-]/g, "")); if (Number.isFinite(n)) { next[f.key] = n; filled++; } }
            else if (f.kind === "select" && f.options?.length) { const m = f.options.find((o) => o.toLowerCase() === v.toLowerCase()); if (m) { next[f.key] = m; filled++; } }
            else { next[f.key] = v; filled++; }
          }
        }
        return next;
      });
      setIdentNote(`${r.note ? r.note + " " : ""}Filled what the photo shows — check it before saving.`.trim());
    } catch (e) {
      setIdentNote(e instanceof BrainError ? e.message : "That failed — please try again.");
    } finally { setIdentBusy(false); }
  }
  const [showPhotoLead, setShowPhotoLead] = useState(Boolean(startWithPhoto) && editing === null);
  async function generateDescription() {
    setDescBusy(true); setDescErr(null);
    try {
      const { text } = await describeItem({ name: name.trim() || "Untitled item", kind: collection.kind, attrs, condition });
      setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    } catch (e) {
      setDescErr(e instanceof BrainError ? e.message : "That failed — please try again.");
    } finally { setDescBusy(false); }
  }

  // iPad/iPhone photo picking. The desktop file dialog (openFileDialog) is a SILENT NO-OP on
  // iOS — tapping "Choose photo" did nothing, the exact Apple 2.1(a) rejection cause. On iOS we
  // trigger a hidden <input type=file accept=image/*>, which opens the native photo/camera sheet
  // (works identically on iPad), then store the picked image as a data URL (rendered already).
  const photoInputRef = useRef<HTMLInputElement>(null);

  async function pickPhoto() {
    setErr(null);
    // platform() is sync, so .click() stays inside the tap's user-gesture (required for the sheet).
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const p = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "gif", "webp", "heic"] }] });
      if (typeof p === "string") setPhotoRef(p);
    } catch { /* user cancelled */ }
  }

  // iOS file-input handler — downscale the picked photo to a modest JPEG data URL kept
  // on-device in photo_ref (never uploaded). Downscaling avoids storing a multi-MB original
  // in the local SQLite row; a catalogue thumbnail only needs ~1024px. Reset value so
  // re-picking the same image refires.
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); return; }
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setPhotoRef(src); return; } // fall back to original on no-canvas
        ctx.drawImage(img, 0, 0, w, h);
        try { setPhotoRef(canvas.toDataURL("image/jpeg", 0.82)); }
        catch { setPhotoRef(src); }
      };
      img.onerror = () => setPhotoRef(src); // unsupported decode (e.g. some HEIC) → keep original
      img.src = src;
    };
    reader.onerror = () => setErr("Could not read that photo — try another.");
    reader.readAsDataURL(file);
  }

  function setAttr(key: string, v: string | number | boolean | null) {
    setAttrs((cur) => ({ ...cur, [key]: v }));
  }

  async function save() {
    if (!name.trim()) { setErr("Give the item a name."); return; }
    setSaving(true); setErr(null);
    // Parse cost: blank → null (no value logged), else a number.
    let cost: number | null = null;
    if (costStr.trim()) {
      const n = Number(costStr.replace(/[^0-9.\-]/g, ""));
      cost = Number.isFinite(n) ? n : null;
    }
    try {
      const payload = {
        name: name.trim(), photo_ref: photoRef.trim(), acquired_on: acquired.trim(),
        source: source.trim(), cost, condition: condition.trim(), wishlist, set_id: setId,
        notes: notes.trim(), attrs,
      };
      if (isEditing) await store.updateItem(editing!.id, payload);
      else await store.createItem({ collection_id: collection.id, ...payload });
      onSaved();
    } catch (e) {
      setErr(`Could not save — ${e instanceof Error ? e.message : String(e)}`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit item" : "Add item"} onClose={onClose}>
      {/* Camera-first: entering from "Add by photo" leads with the picker, and the
          rest of the form waits behind one tap. Editing an existing item keeps the
          plain form — the photo control lives in its usual place below. */}
      {showPhotoLead && (
        <>
          <button className="add-photo-zone" onClick={() => void pickPhoto()}>
            <Camera size={26} />
            <span className="add-photo-title">Add by photo</span>
            <span className="add-photo-sub">kept on device — Identify sends it once</span>
          </button>
          <div style={{ textAlign: "center", margin: "-0.35rem 0 1rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
              or <button className="link-inline" onClick={() => setShowPhotoLead(false)}>fill in the full form</button>
            </span>
          </div>
        </>
      )}

      <Field label="Name">
        <input autoFocus={!showPhotoLead} placeholder="What is it?" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <FieldLabel>Photo <span className="muted" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional, kept on device (Identify sends it once)</span></FieldLabel>
      <div className="row" style={{ gap: "0.6rem", marginBottom: "0.85rem" }}>
        {photoRef ? (
          <img className="thumb" style={{ width: 64, height: 64 }} src={photoRef.startsWith("http") || photoRef.startsWith("data:") ? photoRef : `file://${photoRef}`} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
        ) : (
          <span className="thumb" style={{ width: 64, height: 64 }}><ImageIcon size={22} /></span>
        )}
        <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-sm btn-pill" onClick={pickPhoto}>{photoRef ? "Change" : "Choose photo"}</button>
          {photoRef.startsWith("data:") && (
            <button type="button" className="btn btn-sm btn-pill" disabled={identBusy} onClick={() => void identifyFromPhoto()}
              title="One AI call reads the photo and drafts this collection's own fields — you review before saving. Never a price or appraisal.">
              {identBusy ? "Reading the photo…" : "✨ Fill fields from photo"}
            </button>
          )}
          {photoRef && <button type="button" className="btn-ghost btn-sm" onClick={() => setPhotoRef("")}>Remove</button>}
        </div>
        {identNote && <div className="muted" style={{ fontSize: "0.8rem", width: "100%" }}>{identNote}</div>}
        {/* iOS native photo/camera sheet (iPad-safe). Hidden; triggered by pickPhoto on iOS. */}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Acquired" style={{ flex: 1 }}><input type="date" value={acquired.slice(0, 10)} onChange={(e) => setAcquired(e.target.value)} /></Field>
        <Field label="Value you paid" style={{ flex: 1 }}><input inputMode="decimal" placeholder="e.g. 25" value={costStr} onChange={(e) => setCostStr(e.target.value)} /></Field>
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Source" style={{ flex: 1 }}>
          <input list="curio-sources" placeholder="Where it came from" value={source} onChange={(e) => setSource(e.target.value)} />
          {/* Autocomplete from the sources the user has already used (their own data only). */}
          <datalist id="curio-sources">{knownSources.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        <Field label="Condition" style={{ flex: 1 }}>
          <input list="condition-presets" placeholder="e.g. Mint" value={condition} onChange={(e) => setCondition(e.target.value)} />
          <datalist id="condition-presets">{CONDITION_PRESETS.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
      </div>

      {/* Custom attributes — defined by THIS collection's schema. */}
      {collection.attr_schema.length > 0 && (
        <>
          <FieldLabel>{collection.kind || "Collection"} details</FieldLabel>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.7rem", marginBottom: "0.3rem" }}>
            {/* The collection's OWN field names, worn as kraft specimen-tag chips —
                this schema is the differentiator, so it is labelled as such (R5). */}
            {collection.attr_schema.map((f) => (
              <div key={f.key} style={{ marginBottom: "0.85rem" }}>
                <div style={{ marginBottom: "0.35rem" }}><span className="schema-chip"><span className="k">{f.label}</span></span></div>
                <AttrInput field={f} value={attrs[f.key]} onChange={(v) => setAttr(f.key, v)} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Set membership + wishlist */}
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-end", flexWrap: "nowrap" }}>
        <Field label="Set / series" style={{ flex: 1, marginBottom: 0 }}>
          <select value={setId} onChange={(e) => setSetId(e.target.value)}>
            <option value="">— none —</option>
            {sets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <button type="button" className={`chip${wishlist ? " selected" : ""}`} onClick={() => setWishlist((w) => !w)} style={{ minHeight: 40, gap: "0.3rem" }}>
          <Heart size={13} /> {wishlist ? "On wishlist" : "I have it"}
        </button>
      </div>

      {/* §7b-verified: accurate. signals.rs:33 and the v2-signals schema both exclude free-text
          notes from the aggregates sent to the brain, and this makes no device-locality claim,
          so Cloud Backup does not falsify it. Re-check signals.rs before changing this label. */}
      <Field label="Notes (optional, never sent to AI)" style={{ marginTop: "0.8rem" }}> {/* §7b-verified — signals.rs:33 excludes free text; no device claim made */}
        <textarea rows={2} placeholder="Anything else worth remembering" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ resize: "vertical" }} />
      </Field>

      <div className="row" style={{ gap: "0.5rem", marginTop: "0.4rem" }}>
        <button type="button" className="btn btn-sm btn-pill" onClick={generateDescription} disabled={descBusy || !name.trim()}>
          {descBusy ? "Writing…" : "AI-write description"}
        </button>
      </div>
      {descErr && <div className="muted" style={{ fontSize: "var(--fs-xs)", color: "var(--warning)", marginTop: 4 }}>{descErr}</div>}

      {isEditing && editing && <ProvenanceTimeline itemId={editing.id} />}

      {err && <div className="notice notice-err" style={{ margin: "0.25rem 0" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <div className="row" style={{ gap: "0.6rem", marginTop: "0.6rem", flexWrap: "nowrap" }}>
        <button className="btn btn-pill" onClick={async () => { const n = prompt("New set name"); if (n && n.trim()) { const s = await store.createSet({ collection_id: collection.id, name: n.trim() }); await onSetsChanged(); setSetId(s.id); } }} disabled={saving}>+ Set</button>
        <button className="btn btn-pill" style={{ flex: 1 }} onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={save} disabled={!name.trim() || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Save item"}</button>
      </div>
    </BottomSheet>
  );
}

function AttrInput({ field, value, onChange }: { field: AttrField; value: string | number | boolean | null | undefined; onChange: (v: string | number | boolean | null) => void }) {
  if (field.kind === "number") {
    return <input inputMode="decimal" value={value === null || value === undefined ? "" : String(value)} onChange={(e) => { const t = e.target.value; onChange(t === "" ? null : Number(t.replace(/[^0-9.\-]/g, ""))); }} />;
  }
  if (field.kind === "date") {
    return <input type="date" value={typeof value === "string" ? value.slice(0, 10) : ""} onChange={(e) => onChange(e.target.value || null)} />;
  }
  if (field.kind === "bool") {
    return (
      <button type="button" className={`chip${value === true ? " selected" : ""}`} onClick={() => onChange(value === true ? false : true)}>
        {value === true ? "Yes" : "No"}
      </button>
    );
  }
  if (field.kind === "select") {
    return (
      <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">— choose —</option>
        {(field.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input value={typeof value === "string" ? value : value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value || null)} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sets manager — create / rename / set a target size / delete sets.
// ─────────────────────────────────────────────────────────────────────────────
function SetsManager({ collection, sets, onClose, onChanged }: {
  collection: Collection; sets: CollectionSet[]; onClose: () => void; onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const t = target.trim() ? parseInt(target.trim(), 10) : null;
      await store.createSet({ collection_id: collection.id, name: name.trim(), target_size: Number.isFinite(t as number) ? t : null });
      setName(""); setTarget("");
      await onChanged();
    } finally { setBusy(false); }
  }

  return (
    <BottomSheet title="Sets" onClose={onClose}>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1rem", lineHeight: 1.5 }}>
        Group items into a series and give it a target to track completeness — have X of Y.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", marginBottom: "1rem" }}>
        {sets.map((s) => <SetRow key={s.id} set={s} onChanged={onChanged} />)}
        {sets.length === 0 && <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>No sets yet.</div>}
      </div>
      <div className="card" style={{ padding: "0.7rem", background: "var(--elev-2)", boxShadow: "none" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>New set</div>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <input placeholder="Set name (e.g. Banded agates)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 2 }} />
          <input inputMode="numeric" placeholder="Target" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^0-9]/g, ""))} style={{ flex: 1 }} />
          <button className="btn btn-primary btn-pill" style={{ flexShrink: 0 }} disabled={!name.trim() || busy} onClick={add}>Add</button>
        </div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Done</button>
      </div>
    </BottomSheet>
  );
}

function SetRow({ set, onChanged }: { set: CollectionSet; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(set.name);
  const [target, setTarget] = useState(set.target_size != null ? String(set.target_size) : "");

  async function save() {
    const t = target.trim() ? parseInt(target.trim(), 10) : null;
    await store.updateSet(set.id, { name: name.trim() || set.name, target_size: Number.isFinite(t as number) ? t : null });
    setEditing(false);
    await onChanged();
  }
  async function remove() {
    await store.deleteSet(set.id);
    await onChanged();
  }

  if (editing) {
    return (
      <div className="card" style={{ padding: "0.6rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.4rem" }}>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 2 }} />
          <input inputMode="numeric" placeholder="Target" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^0-9]/g, ""))} style={{ flex: 1 }} />
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
          <button className="btn btn-sm btn-pill" onClick={() => setEditing(false)}>Cancel</button>
          <button className="btn btn-primary btn-sm btn-pill" onClick={save}>Save</button>
        </div>
      </div>
    );
  }
  return (
    <div className="tile">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{set.name}</div>
        <div className="muted tnum" style={{ fontSize: 11 }}>{set.target_size ? `target ${set.target_size}` : "no target"}</div>
      </div>
      <button className="tile-del" title="Edit set" aria-label="Edit set" onClick={() => setEditing(true)}><Pencil size={15} /></button>
      <button className="tile-del" title="Delete set" aria-label="Delete set" onClick={remove}><Trash2 size={15} /></button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Insights — the THREE one-call AI actions. Each button fires its own single billed
// call over the on-device derived-signal slice (signals.rs). Grounded only in the
// user's own catalogue; observational; never states market value; never advises.
// ─────────────────────────────────────────────────────────────────────────────
type InsightErr = { code: BrainErrorCode | "empty"; message: string };

const ACTION_META: Record<CurioAction, { title: string; sub: string; icon: typeof ScrollText; running: string }> = {
  recap: { title: "Recap", sub: "Your numbers, narrated", icon: ScrollText, running: "Counting up your catalogue…" },
  reflect: { title: "Reflect", sub: "What it reveals", icon: Compass, running: "Reading the shape of your collection…" },
  ideas: { title: "Ideas", sub: "Next directions", icon: Lightbulb, running: "Looking for directions in your catalogue…" },
};

function Insights({ collection, hasItems, offline, onClose }: { collection: Collection; hasItems: boolean; offline: boolean; onClose: () => void }) {
  const [action, setAction] = useState<CurioAction | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<CollectionSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<InsightErr | null>(null);

  async function run(a: CurioAction) {
    setAction(a); setBusy(true); setErr(null); setText(null); setFresh(false);
    try {
      // Compute the slice locally first (fast, on-device), then send ONLY that — ONE billed call.
      const sig = await store.computeSignals(collection.id, todayLocal());
      setSignals(sig);
      const r = await runAction(a, sig);
      if (!r.text) setErr({ code: "empty", message: "Nothing came back — try again." });
      else { setText(r.text); setFresh(true); setTimeout(() => setFresh(false), 700); }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setBusy(false); }
  }

  return (
    <BottomSheet title="Insights" onClose={onClose}>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1rem", lineHeight: 1.55 }}>
        Each tour reads a summary of <strong>{collection.name}</strong> — counts, sets, your logged values, the
        fields you fill — grounded only in what you've logged. One Oriora call each.
      </p>

      {offline ? (
        <div className="notice notice-warn"><WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} /><span>You're offline. Reconnect to run an AI tour — your catalogue is safe on this device.</span></div>
      ) : !hasItems ? (
        <div className="card" style={{ padding: "1.85rem 1.25rem", textAlign: "center", boxShadow: "none", background: "var(--elev-2)" }}>
          <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16 }}><Sparkles size={24} /></div>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", margin: "0.7rem 0 0.25rem" }}>Log a few items first</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5, maxWidth: 280, marginInline: "auto" }}>Then Recap, Reflect, and Ideas have something to work with.</div>
        </div>
      ) : (
        <>
          <div className="ai-grid" style={{ marginBottom: "0.4rem" }}>
            {(Object.keys(ACTION_META) as CurioAction[]).map((a) => {
              const m = ACTION_META[a];
              const Icon = m.icon;
              return (
                <button key={a} className={`ai-action${action === a ? " active" : ""}`} disabled={busy} onClick={() => run(a)}>
                  <span className="ai-title"><Icon size={14} /> {m.title}</span>
                  <span className="ai-sub">{m.sub}</span>
                </button>
              );
            })}
          </div>

          {busy && action && (
            <div aria-live="polite" style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
              <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
                <Sparkles size={12} style={{ color: "var(--accent)" }} /> {ACTION_META[action].running}
              </span>
              <div className="skeleton-line" style={{ width: "100%" }} />
              <div className="skeleton-line" style={{ width: "94%" }} />
              <div className="skeleton-line" style={{ width: "72%" }} />
            </div>
          )}

          {err && !busy && <InsightError err={err} onRetry={() => action && run(action)} />}

          {text && !busy && action && (
            <div className={`recap${fresh ? " fresh" : ""} fade-up`} style={{ marginTop: "1rem", padding: "1.1rem 1.25rem" }} aria-live="polite">
              <div className="eyebrow" style={{ marginBottom: "0.5rem" }}>
                {(() => { const Icon = ACTION_META[action].icon; return <Icon size={14} />; })()}
                {ACTION_META[action].title}
              </div>
              <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
              <div style={{ marginTop: 8 }}><OrioraBadge /></div>
              {signals && <FactList signals={signals} />}
              <div className="row" style={{ marginTop: 12 }}>
                <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>Narrates only what you've logged — values are your own entries, not an appraisal.</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.1rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </BottomSheet>
  );
}

function InsightError({ err, onRetry }: { err: InsightErr; onRetry: () => void }) {
  // Insufficient funds (402) — NO amounts shown. iOS must NOT steer to an external purchase
  // (Guideline 3.1.1): point to the in-app Apple IAP top-up. Web/macOS (no IAP) → website.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: "1rem" }}>
        <Coins size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Your Oriora balance is empty</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>
            {platform() === "ios"
              ? "Open the account menu (top-right) and tap Add funds to top up, then try again."
              : "Your Oriora balance is too low. Add funds, then try again."}
          </div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: "1rem" }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: "1rem" }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: "1rem" }}>{err.message}</div>;
  }
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: "1rem", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      {err.code === "unavailable" && (
        <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the pre-ranked headline facts the narration is grounded in (computed in Rust).
// Purely observational — counts, completeness, value extremes from the user's OWN entries.
function FactList({ signals, excludeKinds, standalone = false }: { signals: CollectionSignals; excludeKinds?: string[]; standalone?: boolean }) {
  const facts = signals.headline_facts.filter((f) => !excludeKinds?.includes(f.kind)).slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "good" ? "good" : t === "watch" ? "watch" : "");
  const body = (
    <>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>{standalone ? "More about your collection" : "The numbers behind this"}</div>
      {facts.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </>
  );
  return standalone
    ? <div className="card fade-up" style={{ padding: "0.7rem 1rem", marginTop: "0.7rem" }}>{body}</div>
    : <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>{body}</div>;
}

// ── Small shared bits ────────────────────────────────────────────────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{children}</div>;
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: "0.8rem", ...style }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function FormActions({ onCancel, onSave, saving, canSave, saveLabel }: { onCancel: () => void; onSave: () => void; saving: boolean; canSave: boolean; saveLabel: string }) {
  return (
    <div className="row" style={{ gap: "0.6rem", marginTop: "1.1rem", flexWrap: "nowrap" }}>
      <button className="btn btn-pill" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={onSave} disabled={saving || !canSave}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function EmptyTile({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div className="card" style={{ padding: "1.85rem 1.25rem", textAlign: "center" }}>
      <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16 }}>{icon}</div>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", margin: "0.7rem 0 0.25rem" }}>{title}</div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5, maxWidth: 300, marginInline: "auto" }}>{sub}</div>
    </div>
  );
}

// Bottom sheet for forms — iOS-native feel; slides up from the bottom edge.
export function BottomSheet({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-bottom" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", letterSpacing: "-0.01em" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

// Centered modal — for the small destructive confirm.
function CenterModal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-center" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        {children}
      </div>
    </>
  );
}
